import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getSelectedCompany } from '../../../../store/selectors/AuthSelectors';
import { Spinner, Modal } from 'react-bootstrap';
import PayslipTimeImportModal from './PayslipTimeImportModal';
import PayslipReimbursementsModal from './PayslipReimbursementsModal';
import {
  Plus,
  Trash2,
  Calendar,
  Calculator,
  AlertCircle,
  PlusCircle,
  GripVertical,
  LogOut,
  Receipt,
  FileText,
  Info
} from 'lucide-react';
import * as payrollApi from '../../../../services/api/payrollApi';
import axios from '../../../../services/AxiosInstance';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SelectWageTypeModal from './SelectWageTypeModal';
import EditPayslipRowModal from './EditPayslipRowModal';
import { getAbsenceWageTypes } from './wageTypesConfig';
import { getApiUrl } from '../../../../utils/ApiUtils';
import Swal from 'sweetalert2';
import PageHeader from '../../../layouts/PageHeader';
import Callout from '../../Common/Callout';
import StatusBadge from '../../Common/StatusBadge';
import './payslip.css';
import '../Personell/employee-detail.css';
import '../Personell/employee-form.css';
import { toast } from 'sonner';

// Use comprehensive wage types configuration
const ABSENCE_WAGE_TYPES = getAbsenceWageTypes();

// Helper function to check if a wage type requires absence dates
const requiresAbsenceDates = (wageTypeCode) => {
  return ABSENCE_WAGE_TYPES.includes(wageTypeCode);
};

function Payslip({ match }) {
  const { companyId } = useParams();
  const history = useHistory();
  const company = useSelector(getSelectedCompany);
  const [vacation, setVacation] = useState(null);

  // State management
  const [payslip, setPayslip] = useState(null);
  const [payrollPeriod, setPayrollPeriod] = useState(null);
  const [wageTypes, setWageTypes] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // UI state
  const [isPayslipLoading, setIsPayslipLoading] = useState(true);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [isDeletingRow, setIsDeletingRow] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTimeImport, setShowTimeImport] = useState(false);
  const [timeImportPreview, setTimeImportPreview] = useState(null);
  const [timeImportLoading, setTimeImportLoading] = useState(false);
  const [timeImportError, setTimeImportError] = useState(null);
  const [timeImportImporting, setTimeImportImporting] = useState(false);

  // Termination semesterersättning (slutlön) state
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationDate, setTerminationDate] = useState('');
  const [terminationPreview, setTerminationPreview] = useState(null);
  const [terminationLoading, setTerminationLoading] = useState(false);
  const [terminationApplying, setTerminationApplying] = useState(false);
  const [terminationError, setTerminationError] = useState(null);

  // Reimbursements panel state
  const [showReimbursements, setShowReimbursements] = useState(false);
  const [reimbPreview, setReimbPreview] = useState(null);
  const [reimbLoading, setReimbLoading] = useState(false);
  const [reimbAttaching, setReimbAttaching] = useState(false);
  const [reimbSelected, setReimbSelected] = useState({}); // expenseId → bool
  const [reimbError, setReimbError] = useState(null);

  // Data fetching functions
  const fetchPayslipData = useCallback(async () => {
    setIsPayslipLoading(true);
    try {
      const url = getApiUrl(`${companyId}/payslip/${match.params.id}`);
      const response = await axios.get(url, { withCredentials: true });
      console.log('Payslip data:', response.data);
      
      // Backend now returns data directly in response.data (not wrapped in .data.data)
      const payslipData = response.data.data || response.data;
      setPayslip(payslipData);

      // Also fetch the payroll period to check lock status
      if (payslipData.payrollId) {
        const payrollUrl = getApiUrl(`${companyId}/payroll/${payslipData.payrollId}`);
        const payrollResponse = await axios.get(payrollUrl, { withCredentials: true });
        console.log('Payroll data:', payrollResponse.data);
        
        // Backend returns { status: "success", data: { ... } }
        setPayrollPeriod(payrollResponse.data.data || payrollResponse.data);
      }

    } catch (error) {
      console.error('Error fetching payslip:', error);
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: 'Kunde inte hämta lönespecifikationen'
      // });
      toast.error('Kunde inte hämta lönespecifikationen');
    } finally {
      setIsPayslipLoading(false);
    }
  }, [companyId, match.params.id]);

  const fetchWageTypes = useCallback(async () => {
    try {
      const url = getApiUrl(`${companyId}/payroll/wage_types`);
      const response = await axios.get(url, { withCredentials: true });
      setWageTypes(response.data);
    } catch (error) {
      console.error('Error fetching wage types:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Fel',
        text: 'Kunde inte hämta lönetyper'
      });
    }
  }, [companyId]);

  // Initial data loading
  useEffect(() => {
    Promise.all([fetchPayslipData(), fetchWageTypes()]);
  }, [fetchPayslipData, fetchWageTypes]);

  // Helper functions
  const requiresAbsenceDates = (wageTypeCode) => {
    return ABSENCE_WAGE_TYPES.includes(wageTypeCode);
  };

  const isPayrollLocked = () => {
    return payrollPeriod?.isLocked || false;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Row operations handlers
  const handleAddRow = async (selectedWageTypes) => {
    if (isPayrollLocked()) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Period låst',
      //   text: 'Du kan inte lägga till rader när löneperioden är låst'
      // });
      toast.warning('Du kan inte lägga till rader när löneperioden är låst');
      return;
    }

    setIsAddingRow(true);
    try {
      const newRows = selectedWageTypes.map(wageType => {
        const row = {
          wage_type_id: wageType.wage_type_id || wageType.id, // Support both old and new format
          code: wageType.code,
          description: wageType.description,
          quantity: wageType.quantity || 1,
          inputType: wageType.inputType || 'hours'
        };

        // Add absence dates if they exist (for wage types that require them)
        if (wageType.absenceStartDate) {
          row.absenceStartDate = wageType.absenceStartDate;
        }
        if (wageType.absenceEndDate) {
          row.absenceEndDate = wageType.absenceEndDate;
        }

        return row;
      });

      console.log('Sending wage type rows:', newRows);

      const url = getApiUrl(`${companyId}/payslip/${match.params.id}/rows`);
      await axios.post(
        url,
        { rows: newRows, employeeId: payslip.employeeId },
        { withCredentials: true }
      );

      await fetchPayslipData();
      setShowAddModal(false);

      // Swal.fire({
      //   icon: 'success',
      //   title: 'Tillagt',
      //   text: `${newRows.length} rad(er) har lagts till`,
      //   timer: 1500,
      //   showConfirmButton: false
      // });
      toast.success(`${newRows.length} rad(er) har lagts till`);

    } catch (error) {
      // Show more detailed error message if available
      const errorMessage = error.response?.data?.details || 'Det gick inte att lägga till rader';
      
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: errorMessage
      // });
      toast.error(errorMessage);
    } finally {
      setIsAddingRow(false);
    }
  };

  const handleUpdateRow = async (updatedRow) => {
    if (isPayrollLocked()) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Period låst',
      //   text: 'Du kan inte ändra rader när löneperioden är låst'
      // });
      toast.warning('Du kan inte ändra rader när löneperioden är låst');
      return;
    }

    try {
      const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${updatedRow.rowId || updatedRow.id}`);
      await axios.put(url, {
        employeeId: payslip.employeeId,
        quantity: updatedRow.quantity,
        wageCode: updatedRow.wageType.code,
        inputType: updatedRow.inputType || 'hours',
        absenceStartDate: updatedRow.absenceStartDate || null,
        absenceEndDate: updatedRow.absenceEndDate || null,
        comment: updatedRow.comment || null
      }, { withCredentials: true });

      await fetchPayslipData();
      setShowEditModal(false);

      // Swal.fire({
      //   icon: 'success',
      //   title: 'Uppdaterat',
      //   text: 'Raden har uppdaterats',
      //   timer: 1500,
      //   showConfirmButton: false
      // });
      toast.success('Raden har uppdaterats');
    } catch (error) {
      // console.error('Error updating row:', error);
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: 'Det gick inte att uppdatera raden'
      // });
      toast.error('Det gick inte att uppdatera raden');
    }
  };

  const handleDeleteRow = async () => {
    if (isPayrollLocked()) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Period låst',
      //   text: 'Du kan inte radera rader när löneperioden är låst'
      // });
      toast.warning('Du kan inte radera rader när löneperioden är låst');
      return;
    }

    try {
      const result = await Swal.fire({
        title: 'Är du säker?',
        text: "Denna åtgärd kan inte ångras!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, radera',
        cancelButtonText: 'Avbryt',
        confirmButtonColor: '#dc3545',
        reverseButtons: true
      });

      if (result.isConfirmed) {
        setIsDeletingRow(true);
        const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${selectedItem.rowId || selectedItem.id}`);
        await axios.delete(url, { withCredentials: true });

        await fetchPayslipData();
        setSelectedItem(null);
        setShowEditModal(false);

        // Swal.fire({
        //   icon: 'success',
        //   title: 'Raderat',
        //   text: 'Raden har raderats',
        //   timer: 1500,
        //   showConfirmButton: false
        // });
        toast.success('Raden har raderats');
      }
    } catch (error) {
      console.error('Error deleting row:', error);
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: 'Det gick inte att radera raden'
      // });
      toast.error('Det gick inte att radera raden');
    } finally {
      setIsDeletingRow(false);
    }
  };

  const handleBackToPayroll = () => {
    if (payrollPeriod) {
      history.push(`/${companyId}/payroll/${payrollPeriod.year}/${payrollPeriod.month}`);
    } else {
      history.goBack();
    }
  };

  // Semestersaldo for the sheet's foot. Not part of the payslip payload; the
  // same snapshot the employee detail page shows. Missing data just hides
  // the block, so a failure here never blocks the payslip itself.
  useEffect(() => {
    const employeeId = payslip?.employeeId || payslip?.employee_id;
    if (!employeeId) return undefined;
    let cancelled = false;
    payrollApi.getVacationBalance(companyId, { employeeId })
      .then((res) => { if (!cancelled) setVacation(res.data?.snapshot || null); })
      .catch(() => { if (!cancelled) setVacation(null); });
    return () => { cancelled = true; };
  }, [companyId, payslip?.employeeId, payslip?.employee_id]);

  // Handle drag and drop reordering (visual only - no backend persistence)
  const handleDragEnd = (result) => {
    if (!result.destination || isPayrollLocked()) {
      return;
    }

    const { source, destination } = result;
    
    if (source.index === destination.index) {
      return;
    }

    // Create new array with reordered items
    const newRows = Array.from(payslip.rows || []);
    const [reorderedItem] = newRows.splice(source.index, 1);
    newRows.splice(destination.index, 0, reorderedItem);

    // Update local state for visual reordering
    setPayslip(prev => ({
      ...prev,
      rows: newRows
    }));

    // Show info message that order is visual only
    Swal.fire({
      icon: 'info',
      title: 'Ordning ändrad',
      text: 'Radernas ordning har ändrats visuellt. Denna ordning sparas inte permanent.',
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  };

  if (isPayslipLoading || !payslip) {
    return (
      <div className="text-center py-5 text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Laddar lönespecifikation…
      </div>
    );
  }

  return (
    <>

      {(() => {
        const locked = isPayrollLocked();
        const employeeName = payslip.employee?.fullName || `${payslip.employee?.firstname || ''} ${payslip.employee?.lastname || ''}`.trim();
        const periodLabel = payrollPeriod
          ? new Date(payrollPeriod.year, payrollPeriod.month - 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })
          : 'Aktuell period';
        const rows = payslip.rows || [];
        return (
          <>
            <PageHeader
              backTo={{ to: payrollPeriod ? `/${companyId}/payroll/${payrollPeriod.year}/${payrollPeriod.month}` : `/${companyId}/payroll/periods`, onClick: (e) => { e.preventDefault(); handleBackToPayroll(); }, label: 'Tillbaka till lönekörning' }}
              icon={FileText}
              title="Lönespecifikation"
              subtitle={`${employeeName || 'Anställd'} · ${periodLabel.charAt(0).toUpperCase()}${periodLabel.slice(1)}`}
              actions={
                <>
                  <button
                    type="button"
                    className="ll-btn ll-btn--secondary"
                    onClick={() => {
                      setShowTerminationModal(true);
                      setTerminationPreview(null);
                      setTerminationError(null);
                      const endDate = payslip?.contract?.valid_to || payslip?.contract?.validTo;
                      setTerminationDate(endDate || new Date().toISOString().slice(0, 10));
                    }}
                    disabled={locked}
                    title="Beräkna och bokför slutlön (outtagen semester + sparade dagar), löneart 3232"
                  >
                    <LogOut />
                    Slutlön
                  </button>
                  <button
                    type="button"
                    className="ll-btn ll-btn--primary"
                    onClick={() => setShowAddModal(true)}
                    disabled={isAddingRow || locked}
                  >
                    {isAddingRow ? <Spinner animation="border" size="sm" /> : <PlusCircle />}
                    Lägg till lönerad
                  </button>
                </>
              }
            >
              <div className="emp-detail__meta">
                {locked
                  ? <StatusBadge tone="muted">Period låst</StatusBadge>
                  : <StatusBadge tone="success">Redigerbar</StatusBadge>}
                <span>{formatDate(payslip.periodStartDate)} – {formatDate(payslip.periodEndDate)}</span>
              </div>
            </PageHeader>

            {locked && (
              <Callout tone="warning">
                Löneperioden är låst. Du kan visa lönespecifikationen men inte ändra den. Lås upp perioden i lönekörningen för att redigera.
              </Callout>
            )}

            <div className="slip">
              <div className="slip__top">
                <div>
                  <p className="slip__employer-name">{company?.name || company?.companyName || 'Arbetsgivare'}</p>
                  {company?.registrationNumber && <p className="slip__employer-meta">Org.nr {company.registrationNumber}</p>}
                </div>
                <div className="slip__doc">
                  <p className="slip__doc-title">Lönespecifikation</p>
                  <p className="slip__doc-period">{periodLabel.charAt(0).toUpperCase()}{periodLabel.slice(1)}</p>
                  <p className="slip__doc-meta">
                    Utbetalningsdag {payrollPeriod?.payoutDate ? formatDate(payrollPeriod.payoutDate) : 'ej satt'}
                  </p>
                </div>
              </div>

              <div className="slip__party">
                <div>
                  <span className="slip__label">Anställd</span>
                  <div className="slip__party-value is-strong">{employeeName || '–'}</div>
                </div>
                <div>
                  <span className="slip__label">Personnummer</span>
                  <div className="slip__party-value">{payslip.employee?.ssn || '–'}</div>
                </div>
                <div>
                  <span className="slip__label">Löneperiod</span>
                  <div className="slip__party-value">{formatDate(payslip.periodStartDate)} – {formatDate(payslip.periodEndDate)}</div>
                </div>
                <div>
                  <span className="slip__label">Skattetabell</span>
                  <div className="slip__party-value">
                    {payslip.contractTaxTable ? `${payslip.contractTaxTable}, kolumn ${payslip.contractTaxColumn ?? '–'}` : '–'}
                  </div>
                </div>
                <div>
                  <span className="slip__label">Utbetalas till</span>
                  <div className={`slip__party-value${payslip.bankAccount?.accountNumber ? '' : ' is-muted'}`}>
                    {payslip.bankAccount?.accountNumber
                      ? [payslip.bankAccount.clearingNumber, payslip.bankAccount.accountNumber].filter(Boolean).join('-')
                      : 'Bankkonto saknas'}
                  </div>
                </div>
              </div>

              <div className="slip__rows">
                <div className="slip__row-head" aria-hidden="true">
                  <span />
                  <span className="slip__label" style={{ margin: 0 }}>Löneart</span>
                  <span className="slip__label" style={{ margin: 0 }}>Benämning</span>
                  <span className="slip__label slip__num" style={{ margin: 0 }}>Antal</span>
                  <span className="slip__label slip__num" style={{ margin: 0 }}>À-pris</span>
                  <span className="slip__label slip__num" style={{ margin: 0 }}>Belopp</span>
                </div>

                {rows.length === 0 ? (
                  <div className="slip__empty">
                    Inga lönerader ännu.
                    {!locked && (
                      <>
                        {' '}
                        <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm ms-2" onClick={() => setShowAddModal(true)}>
                          <PlusCircle />
                          Lägg till lönerad
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="salaryItems">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {rows.map((item, index) => {
                            const isDeduction = item.wageType.effectType === 'dec';
                            const hasSub = item.comment
                              || (item.absenceStartDate && item.absenceEndDate)
                              || (requiresAbsenceDates(item.wageType.code) && !item.absenceStartDate);
                            return (
                              <Draggable key={item.rowId || item.id} draggableId={item.rowId || item.id} index={index} isDragDisabled={locked}>
                                {(dragProvided) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    className={`slip__row ${locked ? 'is-locked' : 'is-editable'}`}
                                    onClick={() => {
                                      if (!locked) {
                                        setSelectedItem(item);
                                        setShowEditModal(true);
                                      }
                                    }}
                                  >
                                    <span className="slip__handle" {...dragProvided.dragHandleProps} aria-label="Flytta rad">
                                      <GripVertical size={14} />
                                    </span>
                                    <span className="slip__code">{item.wageType.code}</span>
                                    <div className="slip__desc">
                                      {item.wageType.description}
                                      {hasSub && (
                                        <div className="slip__sub">
                                          {item.comment && <span className="slip__sub-item">{item.comment}</span>}
                                          {item.absenceStartDate && item.absenceEndDate && (
                                            <span className="slip__sub-item">
                                              <Calendar size={12} />
                                              {formatDate(item.absenceStartDate)} – {formatDate(item.absenceEndDate)}
                                            </span>
                                          )}
                                          {requiresAbsenceDates(item.wageType.code) && !item.absenceStartDate && (
                                            <span className="slip__sub-item is-warning">
                                              <AlertCircle size={12} />
                                              Frånvarodatum saknas
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <span className="slip__num" data-label="Antal">{item.quantity} {item.wageType.unit}</span>
                                    <span className="slip__num" data-label="À-pris">{formatCurrency(item.rate)}</span>
                                    <span className={`slip__num${isDeduction ? ' is-deduction' : ''}`} data-label="Belopp">
                                      {isDeduction ? '−' : ''}{formatCurrency(Math.abs(item.amount))}
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>

              <div className="slip__foot">
                <div>
                  <span className="slip__label">Bruttolön</span>
                  <div className="slip__foot-value">{formatCurrency(payslip.grossPay)}</div>
                </div>
                <div>
                  <span className="slip__label">Preliminär skatt</span>
                  <div className="slip__foot-value is-deduction">−{formatCurrency(payslip.tax)}</div>
                </div>
                <div className="slip__foot-net">
                  <span className="slip__label">Att utbetala</span>
                  <div className="slip__foot-value is-net">{formatCurrency(payslip.netPay)}</div>
                </div>
                <div>
                  <span className="slip__label">Arbetsgivaravgifter</span>
                  <div className="slip__foot-value">{formatCurrency(payslip.socialSecContr)}</div>
                </div>
                <div>
                  <span className="slip__label">Total lönekostnad</span>
                  <div className="slip__foot-value">{formatCurrency(Number(payslip.grossPay) + Number(payslip.socialSecContr))}</div>
                </div>
              </div>

              {vacation && (
                <div className="slip__vacation">
                  <span className="slip__label">Semester {vacation.year}/{vacation.year + 1}</span>
                  <div className="slip__vacation-grid">
                    <div>
                      <span className="slip__vacation-label">Betalda dagar kvar</span>
                      <span className="slip__vacation-value is-strong">{Number(vacation.remainingPaidDays || 0)}</span>
                    </div>
                    <div>
                      <span className="slip__vacation-label">Sparade dagar</span>
                      <span className="slip__vacation-value">{Number(vacation.savedDays || 0)}</span>
                    </div>
                    <div>
                      <span className="slip__vacation-label">Uttagna i år</span>
                      <span className="slip__vacation-value">{Number(vacation.usedDays || 0)}</span>
                    </div>
                    <div>
                      <span className="slip__vacation-label">Intjänade</span>
                      <span className="slip__vacation-value">{Number(vacation.earnedDays || 0)}{Number(vacation.carriedOverDays || 0) > 0 ? ` + ${Number(vacation.carriedOverDays)} överförda` : ''}</span>
                    </div>
                    {Number(vacation.advanceDaysTaken || 0) > 0 && (
                      <div>
                        <span className="slip__vacation-label">Förskott</span>
                        <span className="slip__vacation-value is-warning">{Number(vacation.advanceDaysTaken)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        );
      })()}

        {/* Modals */}
        <SelectWageTypeModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          handleSelect={handleAddRow}
          wageTypes={wageTypes}
        />
        <EditPayslipRowModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          item={selectedItem}
          contract={payslip?.contract}
          handleEdit={handleUpdateRow}
          handleDelete={handleDeleteRow}
          isLocked={isPayrollLocked()}
          companyId={companyId}
          employeeId={payslip?.employee_id}
        />

        <TerminationModal
          show={showTerminationModal}
          onClose={() => { setShowTerminationModal(false); setTerminationPreview(null); setTerminationError(null); }}
          terminationDate={terminationDate}
          setTerminationDate={setTerminationDate}
          preview={terminationPreview}
          loading={terminationLoading}
          applying={terminationApplying}
          error={terminationError}
          onPreview={async () => {
            if (!terminationDate) return;
            setTerminationLoading(true); setTerminationError(null);
            try {
              const res = await payrollApi.previewTerminationPayout(companyId, match.params.id, terminationDate);
              setTerminationPreview(res.data?.data || res.data);
            } catch (err) {
              setTerminationError(err.response?.data?.message || 'Kunde inte beräkna slutlön.');
            } finally { setTerminationLoading(false); }
          }}
          onApply={async () => {
            const confirm = await Swal.fire({
              title: 'Bokför slutlön på lönespecen?',
              html: `Lägger till löneart 3232 med ${terminationPreview?.amount || 0} kr. Går endast att ångra via "Ta bort rad".`,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Bokför',
              cancelButtonText: 'Avbryt',
            });
            if (!confirm.isConfirmed) return;
            setTerminationApplying(true); setTerminationError(null);
            try {
              await payrollApi.applyTerminationPayout(companyId, match.params.id, { terminationDate });
              toast.success('Slutlön tillagd på lönespecen.');
              setShowTerminationModal(false);
              await fetchPayslipData();
            } catch (err) {
              setTerminationError(err.response?.data?.message || 'Kunde inte bokföra slutlön.');
            } finally { setTerminationApplying(false); }
          }}
        />
    </>
  );
}

function TerminationModal({
  show, onClose, terminationDate, setTerminationDate,
  preview, loading, applying, error, onPreview, onApply,
}) {
  const fmt = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  return (
    <Modal className="emp-modal" show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Slutlön</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-info">
          <Info size={16} className="info-icon" />
          <p className="info-text">
            Outtagen semester betalas som semesterersättning när anställningen upphör (semesterlagen 28 §)
            och läggs på lönespecen som löneart 3232. Beloppet beräknas enligt procentregeln: kvarvarande dagar × daglön.
          </p>
        </div>

        <div className="form-grid">
          <div className="input-group">
            <label className="input-label" htmlFor="termination-date">
              Slutdatum
              <span className="required-indicator">*</span>
            </label>
            <div className="input-container">
              <input
                id="termination-date"
                type="date"
                className="modern-input"
                value={terminationDate}
                onChange={(e) => setTerminationDate(e.target.value)}
              />
            </div>
          </div>
          <div className="input-group">
            <label className="input-label" aria-hidden="true">&nbsp;</label>
            <button
              type="button"
              className="ll-btn ll-btn--secondary"
              onClick={onPreview}
              disabled={loading || !terminationDate}
            >
              {loading ? <Spinner size="sm" animation="border" /> : <Calculator />}
              Beräkna
            </button>
          </div>
        </div>

        {error && <div className="validation-summary mt-3 mb-0">{error}</div>}

        {preview && (
          <div className="current-value-card mt-3 mb-0" style={{ display: 'block' }}>
            <div className="current-value-label mb-1">Beräkning</div>
            <div className="slip__sum"><span className="slip__sum-label">Kvarvarande semesterdagar</span><span>{fmt(preview.remainingDays)}</span></div>
            <div className="slip__sum"><span className="slip__sum-label">Sparade dagar</span><span>{fmt(preview.savedDays)}</span></div>
            <div className="slip__sum"><span className="slip__sum-label">Betalda dagar totalt</span><span>{fmt(preview.totalDays)}</span></div>
            <div className="slip__sum"><span className="slip__sum-label">Daglön (procentregeln)</span><span>{fmt(preview.dailyValue)} kr</span></div>
            <div className="slip__sum is-net"><span className="slip__sum-label">Semesterersättning</span><span>{fmt(preview.amount)} kr</span></div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose}>Avbryt</button>
        <button
          type="button"
          className="ll-btn ll-btn--primary"
          onClick={onApply}
          disabled={applying || !preview || Number(preview?.amount || 0) <= 0}
        >
          {applying ? <><Spinner size="sm" animation="border" /> Bokför…</> : 'Lägg till på lönespec'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default Payslip;



// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams } from 'react-router-dom';
// import { Card, Table, Container, Row, Col, Spinner, Badge } from 'react-bootstrap';
// import axios from '../../../../services/AxiosInstance';
// import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
// import SelectWageTypeModal from './SelectWageTypeModal';
// import EditPayslipRowModal from './EditPayslipRowModal';
// import { getApiUrl } from '../../../../utils/ApiUtils';
// import Swal from 'sweetalert2';

// // Define wage types that require absence dates (like VAB)
// const ABSENCE_WAGE_TYPES = ['3301', '3305', '3318']; // Update with your actual codes

// function Payslip({ match }) {
//   // Styles remain the same
//   useEffect(() => {
//     const styleSheet = document.createElement('style');
//     styleSheet.textContent = `
//     /* Main container styles */
//     .payslip-wrapper {
//       padding: 20px;
//       background-color: #ffffff;
//     }

//     /* Header section styles */
//     .header-section {
//       margin-bottom: 2rem;
//     }
    
//     .header-title {
//       color: #6c757d;
//       font-size: 1.1rem;
//       font-weight: 500;
//     }

//     /* Information box styles */
//     .info-box {
//       border-top: 1px solid #dee2e6;
//       padding-top: 1rem;
//       margin-bottom: 1.5rem;
//     }

//     .info-label {
//       color: #6c757d;
//       font-size: 0.875rem;
//       display: block;
//       margin-bottom: 0.5rem;
//     }

//     .info-value {
//       font-weight: 300;
//       font-size: 1.1rem;
//       margin-bottom: 0.25rem;
//     }

//     /* Table styles */
//     .payslip-table {
//       margin-top: 1rem;
//     }

//     .payslip-table th {
//       background-color: #f8f9fa;
//       font-weight: 500;
//       padding: 0.75rem;
//       border-bottom: 2px solid #dee2e6;
//     }

//     .payslip-table td {
//       padding: 0.75rem;
//       vertical-align: middle;
//     }

//     .payslip-table tbody tr {
//       cursor: pointer;
//       transition: background-color 0.15s ease-in-out;
//     }

//     .payslip-table tbody tr:hover {
//       background-color: rgba(0, 0, 0, 0.02);
//     }

//     /* Summary section styles */
//     .summary-table td {
//       padding: 1rem;
//       border-top: 1px solid #dee2e6;
//     }

//     .summary-label {
//       text-transform: uppercase;
//       font-size: 0.875rem;
//       color: #495057;
//     }

//     .summary-value {
//       text-align: right;
//       font-size: 1.25rem;
//       font-weight: 500;
//     }

//     /* Stats section styles */
//     .stats-section {
//       margin-top: 2rem;
//     }

//     .stats-box {
//       border-top: 1px solid #dee2e6;
//       padding-top: 1rem;
//     }

//     .stats-title {
//       font-size: 1rem;
//       font-weight: 500;
//       margin-bottom: 1rem;
//     }

//     .stats-row {
//       display: flex;
//       justify-content: space-between;
//       margin-bottom: 0.5rem;
//     }

//     /* Button styles */
//     .add-button {
//       min-width: 150px;
//       margin-top: 1rem;
//     }

//     /* Loading spinner styles */
//     .loading-container {
//       display: flex;
//       justify-content: center;
//       align-items: center;
//       min-height: 200px;
//     }

//     /* Drag handle styles */
//     .drag-handle {
//       color: #6c757d;
//       cursor: grab;
//     }

//     .drag-handle:active {
//       cursor: grabbing;
//     }
    
//     /* New styles for absence dates */
//     .absence-badge {
//       font-size: 0.75rem;
//       margin-left: 0.5rem;
//     }
    
//     .comment-text {
//       color: #6c757d;
//       font-style: italic;
//       font-size: 0.85rem;
//       margin-top: 0.25rem;
//       display: block;
//     }
//     `;
//     document.head.appendChild(styleSheet);

//     // Cleanup on unmount
//     return () => {
//       document.head.removeChild(styleSheet);
//     };
//   }, []);


//   const { companyId } = useParams();

//   // State management
//   const [payslip, setPayslip] = useState(null);
//   const [wageTypes, setWageTypes] = useState([]);
//   const [selectedItem, setSelectedItem] = useState(null);

//   // UI state
//   const [isPayslipLoading, setIsPayslipLoading] = useState(true);
//   const [isAddingRow, setIsAddingRow] = useState(false);
//   const [isDeletingRow, setIsDeletingRow] = useState(false);
//   const [showAddModal, setShowAddModal] = useState(false);
//   const [showEditModal, setShowEditModal] = useState(false);

//   // Data fetching functions
//   const fetchPayslipData = useCallback(async () => {
//     setIsPayslipLoading(true);
//     try {
//       const url = getApiUrl(`${companyId}/payslip/${match.params.id}`);
//       const response = await axios.get(url, { withCredentials: true });
//       console.log('Payslip data:', response.data);
//       setPayslip(response.data);
//     } catch (error) {
//       console.error('Error fetching payslip:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Kunde inte hämta lönespecifikationen'
//       });
//     } finally {
//       setIsPayslipLoading(false);
//     }
//   }, [companyId, match.params.id]);

//   const fetchWageTypes = useCallback(async () => {
//     try {
//       const url = getApiUrl(`${companyId}/payroll/wage_types`);
//       const response = await axios.get(url, { withCredentials: true });
//       setWageTypes(response.data);
//     } catch (error) {
//       console.error('Error fetching wage types:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Kunde inte hämta lönetyper'
//       });
//     }
//   }, [companyId]);

//   // Initial data loading
//   useEffect(() => {
//     Promise.all([fetchPayslipData(), fetchWageTypes()]);
//   }, [fetchPayslipData, fetchWageTypes]);

//   // Helper to check if a wage type requires absence dates
//   const requiresAbsenceDates = (wageTypeCode) => {
//     return ABSENCE_WAGE_TYPES.includes(wageTypeCode);
//   };

//   // Row operations handlers
//   const handleAddRow = async (selectedWageTypes) => {
//     setIsAddingRow(true);
//     try {
//       const newRows = selectedWageTypes.map(wageType => ({
//         wage_type_id: wageType.wage_type_id,
//         code: wageType.code,
//         description: wageType.description,
//       }));

//       const url = getApiUrl(`${companyId}/payslip/${match.params.id}/rows`);
//       await axios.post(
//         url,
//         { rows: newRows, employeeId: payslip.values.employeeId },
//         { withCredentials: true }
//       );

//       await fetchPayslipData();
//       setShowAddModal(false);

//     } catch (error) {
//       console.error('Error adding rows:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Det gick inte att lägga till rader'
//       });
//     } finally {
//       setIsAddingRow(false);
//     }
//   };

//   const handleUpdateRow = async (updatedRow) => {
//     try {
//       const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${updatedRow.id}`);
//       await axios.put(url, {
//         employeeId: payslip.values.employeeId,
//         quantity: updatedRow.quantity,
//         wageCode: updatedRow.wageType.code,
//         // Add new fields for absence dates and comments
//         absenceStartDate: updatedRow.absenceStartDate || null,
//         absenceEndDate: updatedRow.absenceEndDate || null,
//         comment: updatedRow.comment || null
//       }, { withCredentials: true });

//       await fetchPayslipData();
//       setShowEditModal(false);
//     } catch (error) {
//       console.error('Error updating row:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Det gick inte att uppdatera raden'
//       });
//     }
//   };

//   const handleDeleteRow = async () => {
//     try {
//       const result = await Swal.fire({
//         title: 'Är du säker?',
//         text: "Denna åtgärd kan inte ångras!",
//         icon: 'warning',
//         showCancelButton: true,
//         confirmButtonText: 'Ja, radera',
//         cancelButtonText: 'Avbryt',
//         confirmButtonColor: '#dc3545',
//         reverseButtons: true
//       });

//       if (result.isConfirmed) {
//         setIsDeletingRow(true);
//         const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${selectedItem.id}`);
//         await axios.delete(url, { withCredentials: true });

//         await fetchPayslipData();
//         setSelectedItem(null);
//         setShowEditModal(false);
//       }
//     } catch (error) {
//       console.error('Error deleting row:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Det gick inte att radera raden'
//       });
//     } finally {
//       setIsDeletingRow(false);
//     }
//   };

//   // Format date for display
//   const formatDate = (dateString) => {
//     if (!dateString) return '';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('sv-SE'); // Format as YYYY-MM-DD
//   };

//   if (isPayslipLoading || !payslip) {
//     return (
//       <div className="loading-container">
//         <Spinner animation="border" role="status">
//           <span className="visually-hidden">Laddar...</span>
//         </Spinner>
//       </div>
//     );
//   }

//   return (
//     <div className="payslip-wrapper">
//       {/* Header Section */}
//       <div className="header-section">
//         <Row className="align-items-center">
//           <Col>
//             <h6 className="header-title">
//               Lönespecifikation för perioden {new Date().getFullYear()} {new Date().toLocaleString('sv-SE', { month: 'long' })}
//             </h6>
//           </Col>
//           <Col className="text-end">
//             <h6 className="header-title">{payslip.companyName}</h6>
//           </Col>
//         </Row>
//       </div>

//       {/* Employee Information */}
//       <Row className="mb-4">
//         <Col>
//           <div className="info-box">
//             <span className="info-label">Betalas till</span>
//             <h6 className="info-value">{payslip.values.name}</h6>
//             <h6 className="info-value">{payslip.values.ssn}</h6>
//           </div>
//         </Col>
//         <Col>
//           <div className="info-box">
//             <span className="info-label">Skatt</span>
//             <h6 className="info-value">Skattetabell: {payslip.values.taxTable}</h6>
//             <h6 className="info-value">Skattekolumn: {payslip.values.taxColumn}</h6>
//           </div>
//         </Col>
//       </Row>

//       {/* Payslip Rows */}
//       <Card>
//         <Card.Body>
//           <DragDropContext onDragEnd={() => { }}>
//             <Droppable droppableId="salaryItems">
//               {(provided) => (
//                 <Table className="payslip-table" responsive ref={provided.innerRef} {...provided.droppableProps}>
//                   <thead>
//                     <tr>
//                       <th></th>
//                       <th>Benämning</th>
//                       <th>Antal</th>
//                       <th>Kostnad</th>
//                       <th>Summa</th>
//                       <th>Frånvaroperiod</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {payslip.paySlipRows.map((item, index) => (
//                       <Draggable key={item.id} draggableId={item.id} index={index}>
//                         {(provided) => (
//                           <tr
//                             ref={provided.innerRef}
//                             {...provided.draggableProps}
//                             {...provided.dragHandleProps}
//                             onClick={() => {
//                               setSelectedItem(item);
//                               setShowEditModal(true);
//                             }}
//                           >
//                             <td>
//                               <i className="bi bi-list-ul drag-handle"></i>
//                             </td>
//                             <td>
//                               {item.wageType.code}, {item.wageType.description}
//                               {item.comment && (
//                                 <span className="comment-text">{item.comment}</span>
//                               )}
//                             </td>
//                             <td>{item.quantity} {item.wageType.wageTypeUnit}</td>
//                             <td>{item.rate}</td>
//                             <td>
//                               {item.wageType.wageTypeEffectType === 'dec' ? -item.amount : item.amount}
//                             </td>
//                             <td>
//                               {item.absenceStartDate && item.absenceEndDate ? (
//                                 `${formatDate(item.absenceStartDate)} - ${formatDate(item.absenceEndDate)}`
//                               ) : (
//                                 requiresAbsenceDates(item.wageType.code) ? (
//                                   <Badge bg="warning" pill>Saknas</Badge>
//                                 ) : null
//                               )}
//                             </td>
//                           </tr>
//                         )}
//                       </Draggable>
//                     ))}
//                     {provided.placeholder}
//                   </tbody>
//                 </Table>
//               )}
//             </Droppable>
//           </DragDropContext>

//           <button
//             onClick={() => setShowAddModal(true)}
//             className="btn btn-primary add-button"
//             disabled={isAddingRow}
//           >
//             {isAddingRow ? (
//               <>
//                 <Spinner size="sm" className="me-2" />
//                 Lägger till...
//               </>
//             ) : (
//               'Lägg till ny rad'
//             )}
//           </button>
//         </Card.Body>
//       </Card>

//       {/* Summary Section */}
//       <Card className="mt-4">
//         <Card.Body>
//           <Table className="summary-table">
//             <tbody>
//               <tr>
//                 <td className="summary-label">Total bruttolön för perioden</td>
//                 <td className="summary-value">{payslip.values.grossIncome} kr</td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Din skatt enligt tabell</td>
//                 <td className="summary-value">{payslip.values.taxTotal} kr</td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Sociala avgifter</td>
//                 <td className="summary-value">{payslip.values.socialTaxTotal} kr</td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Total lönekostnad för företaget</td>
//                 <td className="summary-value">
//                   {(Number(payslip.values.grossIncome) + Number(payslip.values.socialTaxTotal)).toFixed(2)} kr
//                 </td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Nettolön</td>
//                 <td className="summary-value">{payslip.values.netTotal} kr</td>
//               </tr>
//             </tbody>
//           </Table>
//         </Card.Body>
//       </Card>

//       {/* Statistics Section */}
//       <div className="stats-section">
//         <Row>
//           <Col md={6}>
//             <div className="stats-box">
//               <h6 className="stats-title">Semesterdagar</h6>
//               <div className="stats-row">
//                 <span>Betalda:</span>
//                 <span>{payslip.vacationDays.allowed}</span>
//               </div>
//               <div className="stats-row">
//                 <span>Sparade:</span>
//                 <span>{payslip.vacationDays.allowed - payslip.vacationDays.taken}</span>
//               </div>
//             </div>
//           </Col>
//           <Col md={6}>
//             <div className="stats-box">
//               <h6 className="stats-title">Totalt detta år</h6>
//               <div className="stats-row">
//                 <span>Brutto:</span>
//                 <span>{payslip.yearlyTotals.salaryTotal} kr</span>
//               </div>
//               <div className="stats-row">
//                 <span>Skatt:</span>
//                 <span>{payslip.yearlyTotals.taxTotal} kr</span>
//               </div>
//               <div className="stats-row">
//                 <span>Förmåner:</span>
//                 <span>{payslip.yearlyTotals.benefits} kr</span>
//               </div>
//             </div>
//           </Col>
//         </Row>
//       </div>

//       {/* Modals */}
//       <SelectWageTypeModal
//         show={showAddModal}
//         handleClose={() => setShowAddModal(false)}
//         handleSelect={handleAddRow}
//         wageTypes={wageTypes}
//       />
//       <EditPayslipRowModal
//         show={showEditModal}
//         handleClose={() => setShowEditModal(false)}
//         item={selectedItem}
//         handleEdit={handleUpdateRow}
//         handleDelete={handleDeleteRow}
//       />
//     </div>
//   );
// }

// export default Payslip;